// Ishihara colour-vision plates and scoring. Straight port of the dashboard's
// features/engage-prescreening/utils/ishihara.ts (itself a port of the legacy pages/IshiharaTest.js) so a
// clinician-led Colour Vision test stores result_data in the SAME shape as the async pre-screening test:
//   { "<plateId>": { plate: {...}, result: 'Correct' | 'Incorrect' | '', selected: '<answer>' }, ... }
// Plate images are served from public/img/ishihara (copied from ibis-dashboard/public/img/ishihara).

export const ISHIHARA_UNCLEAR = 'unclear';

export const ishiharaFamiliarisationPlate = { id: '01', options: ['12', '37'] };

export const ishiharaPlates = [
  { id: '02', answer: '8', options: ['8', '3'], penalty_answer: '3' },
  { id: '03', answer: '5', options: ['5', '2'], penalty_answer: '2' },
  { id: '04', answer: '29', options: ['29', '70'], penalty_answer: '70' },
  { id: '05', answer: '74', options: ['74', '21'], penalty_answer: '21' },
  { id: '06', answer: '7', options: ['7', '4'], penalty_answer: ISHIHARA_UNCLEAR },
  { id: '07', answer: '45', options: ['45', '73'], penalty_answer: ISHIHARA_UNCLEAR },
  { id: '08', answer: '2', options: ['2', '8'], penalty_answer: '8' },
  { id: '09', answer: ISHIHARA_UNCLEAR, options: ['8', '2'], penalty_answer: '2' },
  { id: '10', answer: '16', options: ['16', '33'], penalty_answer: ISHIHARA_UNCLEAR },
];

const MOST_DISTINCT = 'Indicate if you see a number on the plate.  If you see more than one number please select the most distinct.';

export const fixedOrderPlates = [
  { id: '11', answer: 'Yes', options: ['Yes', 'No'], custom_instructions: 'Can you see a path traced from left to right across the plate?' },
];

export const ishiharaAdditionalPlates = [
  { id: '12', answer: '3 or 5', options: ['5', '3'], custom_instructions: MOST_DISTINCT },
  { id: '13', answer: '9 or 6', options: ['6', '9'], custom_instructions: MOST_DISTINCT },
];

export const ishiharaPlateImage = (plateId) => `${process.env.PUBLIC_URL || ''}/img/ishihara/testplate_${plateId}.png`;

/** Non-mutating Fisher-Yates shuffle. */
export const shuffle = (items, random = Math.random) => {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

/** Familiarisation plate, shuffled core plates, fixed-order plate, then the additional plates. */
export const buildIshiharaSequence = (random = Math.random) => [
  ishiharaFamiliarisationPlate,
  ...shuffle(ishiharaPlates, random),
  ...fixedOrderPlates,
  ...ishiharaAdditionalPlates,
];

export const scoreIshiharaAnswer = (plate, selected) => {
  if (selected === plate.answer) return { plate, result: 'Correct', selected };
  if (selected === plate.penalty_answer) return { plate, result: 'Incorrect', selected };
  return { plate, result: '', selected };
};

export const countIshiharaResults = (answers) => {
  const values = Object.values(answers);
  return {
    correct: values.filter((answer) => answer.result === 'Correct').length,
    incorrect: values.filter((answer) => answer.result === 'Incorrect').length,
  };
};

/** Legacy early stop: 8+ correct with fewer than 2 incorrect. */
export const shouldEndIshiharaEarly = (answers) => {
  const { correct, incorrect } = countIshiharaResults(answers);
  return correct >= 8 && incorrect < 2;
};

/** Plates whose options are words (plate 11 "Yes"/"No") are answered with buttons, not the keypad. */
export const isKeypadPlate = (plate) => plate.options.every((option) => /^\d+$/.test(option));

/** Human label for a stored answer. */
export const formatIshiharaAnswer = (value) => (value === ISHIHARA_UNCLEAR ? 'Nothing seen' : value || '—');
